import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { api } from '../../lib/api';

type Counts = Record<string, number>;
type AssignedTable = { id: string; tableNo: number; maxSeats: number };
const legacyFaces = [100, 500, 2500, 10000, 50000];
const zeros = (faces: number[]): Counts =>
  Object.fromEntries(faces.map((face) => [String(face), 0]));
interface Seat {
  memberNo: string;
  identity?: { displayName: string; phoneSuffix: string | null };
  tableId: string | null;
  tableNo?: number | null;
  seatNo: number | null;
  countRevisionId: string | null;
  chipCounts: Counts | null;
  totalChips: number | null;
  history: Array<{ id: string; chipCounts: Counts; reason: string | null; countedAt: string }>;
}

const FIELD = 'mt-1 h-11 w-full border border-base-300 p-2';
const PRIMARY =
  'min-h-11 px-4 py-2 bg-primary text-primary-content font-medium hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50';
const OUTLINE =
  'inline-flex min-h-11 items-center border border-base-300 px-4 py-2 text-sm text-base-content hover:bg-base-300';
const TABS = [
  ['seats', 'seatsTab'],
  ['actions', 'playerActionsTab'],
  ['transfer', 'transferTab'],
] as const;

/** One labelled count field per chip denomination. */
export function ChipCountFields({
  faces,
  counts,
  locale,
  onChange,
}: {
  faces: number[];
  counts: Counts;
  locale: string;
  onChange: (face: number, value: number) => void;
}) {
  const { t } = useTranslation('venue');
  return (
    <div className="grid grid-cols-2 gap-2">
      {faces.map((face) => (
        <label key={face} className="text-sm text-base-content">
          {t('timedCount.denominationCount', { face: face.toLocaleString(locale) })}
          <input
            type="number"
            inputMode="numeric"
            min="0"
            step="1"
            className={`${FIELD} text-right tabular-nums`}
            value={counts[String(face)] ?? 0}
            onChange={(event) => onChange(face, Number(event.target.value))}
          />
        </label>
      ))}
    </div>
  );
}

const seatIdentity = (seat: Seat) => {
  const name = seat.identity?.displayName || seat.memberNo;
  const phoneLastFour = seat.identity?.phoneSuffix?.slice(-4);
  return phoneLastFour ? `${name} / ${phoneLastFour}` : name;
};

export default function TimedTableCountPanel({
  tournamentId,
  tableId,
  tableNo,
  maxSeats,
  canCount,
  assignedTables,
}: {
  tournamentId: string;
  tableId: string;
  tableNo: number;
  maxSeats: number;
  canCount: boolean;
  assignedTables: AssignedTable[];
}) {
  const { t, i18n } = useTranslation('venue');
  const locale = i18n.language;
  const qc = useQueryClient();
  const [activeTab, setActiveTab] = useState<'seats' | 'actions' | 'transfer'>('seats');
  const [seatNo, setSeatNo] = useState<number | null>(null);
  const [memberNo, setMemberNo] = useState('');
  const [selected, setSelected] = useState('');
  const [counts, setCounts] = useState<Counts>({});
  const [countsDirty, setCountsDirty] = useState(false);
  const [reason, setReason] = useState('');
  const [seatReason, setSeatReason] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [transferMemberNo, setTransferMemberNo] = useState('');
  const [targetTableId, setTargetTableId] = useState('');
  const [targetSeatNo, setTargetSeatNo] = useState<number | null>(null);
  const [transferReason, setTransferReason] = useState('');
  const { data, isLoading } = useQuery({
    queryKey: ['timed-table-counts', tournamentId],
    queryFn: () =>
      api.get<{ seats: Seat[]; denominations: number[] }>(
        `/tournament/${tournamentId}/timed-settlement/table-counts`,
      ),
    refetchInterval: 5000,
  });
  const faces = data?.denominations ?? legacyFaces;
  const tableSeats = data?.seats.filter((seat) => seat.tableId === tableId) ?? [];
  const selectedSeat = tableSeats.find((seat) => seat.memberNo === selected);
  const priorSeat = data?.seats.find((seat) => seat.memberNo === memberNo);
  const moving = Boolean(
    priorSeat?.tableId && (priorSeat.tableId !== tableId || priorSeat.seatNo !== seatNo),
  );
  const targetTable = assignedTables.find((table) => table.id === targetTableId);
  const targetSeats = targetTable
    ? Array.from({ length: targetTable.maxSeats }, (_, index) => index + 1).filter(
        (number) =>
          !data?.seats.some((seat) => seat.tableId === targetTableId && seat.seatNo === number),
      )
    : [];
  const changeTab = (tab: 'seats' | 'actions' | 'transfer') => {
    if (tab !== activeTab && countsDirty && !window.confirm(t('timedCount.confirmLeaveTab')))
      return;
    if (tab !== activeTab) setCountsDirty(false);
    setActiveTab(tab);
    setError('');
  };
  useEffect(() => {
    if (countsDirty) return;
    setCounts({ ...zeros(faces), ...selectedSeat?.chipCounts });
    setReason('');
  }, [selected, selectedSeat?.countRevisionId, selectedSeat?.chipCounts, faces, countsDirty]);
  const chooseSeat = (number: number) => {
    if (countsDirty && !window.confirm(t('timedCount.confirmLeaveSeat'))) return;
    const occupied = tableSeats.find((seat) => seat.seatNo === number);
    setSeatNo(number);
    setSelected(occupied?.memberNo ?? '');
    setMemberNo('');
    setCountsDirty(false);
    setCounts({ ...zeros(faces), ...occupied?.chipCounts });
    setReason('');
    setSeatReason('');
    setError('');
    setSuccess('');
  };
  const refresh = async () => {
    await qc.invalidateQueries({ queryKey: ['timed-table-counts', tournamentId] });
    await qc.invalidateQueries({ queryKey: ['timed-settlement', tournamentId] });
  };
  const assign = useMutation({
    mutationFn: () =>
      api.put(`/tournament/${tournamentId}/timed-settlement/table-seats`, {
        memberNo,
        tableId,
        seatNo,
        reason: seatReason.trim() || undefined,
      }),
    onSuccess: async () => {
      setSelected(memberNo);
      setMemberNo('');
      setSeatReason('');
      setError('');
      setSuccess(t('timedCount.assigned'));
      await refresh();
    },
    onError: (failure: Error) => setError(failure.message),
  });
  const save = useMutation({
    mutationFn: () =>
      api.put(`/tournament/${tournamentId}/timed-settlement/table-counts`, {
        memberNo: selected,
        chipCounts: counts,
        reason: reason.trim() || undefined,
      }),
    onSuccess: async () => {
      setCountsDirty(false);
      setReason('');
      setError('');
      setSuccess(t('timedCount.saved'));
      await refresh();
    },
    onError: (failure: Error) => setError(failure.message),
  });
  const transfer = useMutation({
    mutationFn: () =>
      api.put(`/tournament/${tournamentId}/timed-settlement/table-seats`, {
        memberNo: transferMemberNo,
        tableId: targetTableId,
        seatNo: targetSeatNo,
        reason: transferReason.trim(),
      }),
    onSuccess: async () => {
      setTransferMemberNo('');
      setTargetTableId('');
      setTargetSeatNo(null);
      setTransferReason('');
      setSelected('');
      setSeatNo(null);
      setError('');
      setSuccess(t('timedCount.transferred'));
      setActiveTab('seats');
      await refresh();
    },
    onError: (failure: Error) => setError(failure.message),
  });
  const total = faces.reduce((sum, face) => sum + face * (counts[String(face)] ?? 0), 0);
  const valid = faces.every(
    (face) => Number.isSafeInteger(counts[String(face)]) && (counts[String(face)] ?? -1) >= 0,
  );
  const figure = (value: number | null | undefined) => value?.toLocaleString(locale);
  const seatStatus = (seat: Seat) =>
    seat.countRevisionId
      ? t('timedCount.seatCounted', { total: figure(seat.totalChips) })
      : t('timedCount.notCounted');
  return (
    <section
      className="space-y-4 border border-base-300 p-3"
      aria-label={t('timedCount.regionLabel')}
    >
      <div>
        <h3 className="font-semibold text-base-content">{t('timedCount.title', { tableNo })}</h3>
        <p className="text-sm text-base-content/70">{t('timedCount.intro')}</p>
        {!canCount && (
          <p role="status" className="mt-2 border border-warning p-2 text-sm text-base-content">
            {t('timedCount.lockedNotice')}
          </p>
        )}
      </div>
      <div
        className="flex border-b border-base-300"
        role="tablist"
        aria-label={t('timedCount.tabsLabel')}
      >
        {TABS.map(([tab, label]) => (
          <button
            key={tab}
            id={`timed-tab-${tab}`}
            type="button"
            role="tab"
            aria-selected={activeTab === tab}
            aria-controls={`timed-panel-${tab}`}
            className={`min-h-12 flex-1 border-b-2 px-1 text-sm ${activeTab === tab ? 'border-primary text-primary font-medium' : 'border-transparent text-base-content/70 hover:text-base-content'}`}
            onClick={() => changeTab(tab)}
          >
            {t(label)}
          </button>
        ))}
      </div>
      {activeTab === 'seats' && (
        <div
          className="space-y-4"
          role="tabpanel"
          id="timed-panel-seats"
          aria-labelledby="timed-tab-seats"
        >
          <div
            role="group"
            className="relative bg-base-300 p-4"
            aria-label={t('timedCount.seatMapLabel', { tableNo })}
          >
            <div
              aria-hidden="true"
              className="absolute inset-8 rounded-[50%] border-2 border-primary/20 bg-primary/5"
            />
            <div aria-hidden="true" className="absolute inset-0 flex items-center justify-center">
              <span className="text-xs font-medium text-base-content/70">TABLE {tableNo}</span>
            </div>
            <div className="relative w-full pt-[140%]">
              <div
                aria-hidden="true"
                className="absolute z-10 -translate-x-1/2 -translate-y-1/2"
                style={{ left: '50%', top: '12%' }}
              >
                <div className="border border-warning bg-base-100 px-3 py-1 text-center text-xs font-bold text-base-content">
                  DEALER
                </div>
              </div>
              {Array.from({ length: maxSeats }, (_, index) => index + 1).map((number) => {
                const occupant = tableSeats.find((seat) => seat.seatNo === number);
                const angle =
                  ((-90 + 35 + (290 / (maxSeats - 1 || 1)) * (number - 1)) * Math.PI) / 180;
                const x = 50 + 44 * Math.cos(angle);
                const y = 50 + 38 * Math.sin(angle);
                const phoneLastFour = occupant?.identity?.phoneSuffix?.slice(-4);
                return (
                  <div
                    key={number}
                    className="absolute -translate-x-1/2 -translate-y-1/2"
                    style={{ left: `${x}%`, top: `${y}%` }}
                  >
                    <button
                      type="button"
                      disabled={!canCount}
                      aria-pressed={seatNo === number}
                      aria-label={`${t('timedCount.seatNo', { seatNo: number })} ${occupant ? `${seatIdentity(occupant)} · ${seatStatus(occupant)}` : t('seatLegend.empty')}`}
                      className={`min-h-12 w-16 border px-1 py-1 text-center text-xs leading-tight text-base-content transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${seatNo === number ? 'border-2 border-primary bg-primary/10' : occupant ? 'border-success bg-success/10 hover:bg-success/20' : 'border-dashed border-base-content/40 bg-base-100 hover:bg-base-200'}`}
                      onClick={() => chooseSeat(number)}
                    >
                      <span className="block tabular-nums text-base-content/70">#{number}</span>
                      {occupant ? (
                        <>
                          <span
                            className="block truncate font-medium"
                            title={seatIdentity(occupant)}
                          >
                            {occupant.identity?.displayName || occupant.memberNo}
                          </span>
                          {phoneLastFour && <span className="block">/ {phoneLastFour}</span>}
                          <span className="block truncate tabular-nums">
                            {occupant.countRevisionId
                              ? figure(occupant.totalChips)
                              : t('timedCount.notCounted')}
                          </span>
                        </>
                      ) : (
                        <span className="block text-base-content/70">{t('seatLegend.empty')}</span>
                      )}
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
          {isLoading && (
            <p role="status" className="text-sm text-base-content/70">
              {t('timedCount.loadingSeats')}
            </p>
          )}
          {seatNo !== null && !selectedSeat && canCount && (
            <div className="space-y-2 border border-base-300 p-3">
              <h4 className="font-medium text-base-content">
                {t('timedCount.assignHeading', { seatNo })}
              </h4>
              <label className="block text-sm text-base-content">
                {t('timedCount.savedMembers')}
                <select
                  className={FIELD}
                  value={memberNo}
                  onChange={(event) => setMemberNo(event.target.value)}
                >
                  <option value="">{t('timedCount.chooseMember')}</option>
                  {data?.seats.map((seat) => (
                    <option key={seat.memberNo} value={seat.memberNo}>
                      {seatIdentity(seat)}
                      {seat.tableId ? ` · ${t('timedCount.alreadySeated')}` : ''}
                    </option>
                  ))}
                </select>
              </label>
              {moving && (
                <label className="block text-sm text-base-content">
                  {t('timedCount.moveReason')}
                  <input
                    className={FIELD}
                    value={seatReason}
                    onChange={(event) => setSeatReason(event.target.value)}
                  />
                </label>
              )}
              <button
                type="button"
                className={PRIMARY}
                disabled={!memberNo || (moving && !seatReason.trim()) || assign.isPending}
                onClick={() => assign.mutate()}
              >
                {t('timedCount.assignTo', { seatNo })}
              </button>
            </div>
          )}
          {selectedSeat && canCount && (
            <div className="space-y-3 border border-base-300 p-3">
              <h4 className="font-medium text-base-content">
                {t('timedCount.countHeading', { seatNo, player: seatIdentity(selectedSeat) })}
              </h4>
              <ChipCountFields
                faces={faces}
                counts={counts}
                locale={locale}
                onChange={(face, value) => {
                  setCounts((current) => ({ ...current, [face]: value }));
                  setCountsDirty(true);
                  setSuccess('');
                }}
              />
              <p className="text-lg font-semibold tabular-nums text-base-content">
                {t('timedCount.total', { total: figure(total) })}
              </p>
              <label className="block text-sm text-base-content">
                {selectedSeat.history.length
                  ? t('timedCount.correctionReason')
                  : t('timedCount.countNote')}
                <input
                  className={FIELD}
                  value={reason}
                  onChange={(event) => setReason(event.target.value)}
                />
              </label>
              <button
                type="button"
                className={PRIMARY}
                disabled={
                  !valid || (selectedSeat.history.length > 0 && !reason.trim()) || save.isPending
                }
                onClick={() => save.mutate()}
              >
                {t('timedCount.saveCount')}
              </button>
              {selectedSeat.history.length > 0 && (
                <details className="text-sm text-base-content">
                  <summary className="flex min-h-11 cursor-pointer items-center">
                    {t('timedCount.historySummary', { count: selectedSeat.history.length })}
                  </summary>
                  <ul className="mt-2 space-y-1 tabular-nums">
                    {selectedSeat.history.map((item) => (
                      <li key={item.id}>
                        {new Date(item.countedAt).toLocaleString(locale)} ·{' '}
                        {figure(
                          faces.reduce(
                            (sum, face) => sum + face * (item.chipCounts[String(face)] ?? 0),
                            0,
                          ),
                        )}{' '}
                        · {item.reason || t('timedCount.firstCount')}
                      </li>
                    ))}
                  </ul>
                </details>
              )}
            </div>
          )}
        </div>
      )}
      {activeTab === 'actions' && (
        <div
          className="space-y-3"
          role="tabpanel"
          id="timed-panel-actions"
          aria-labelledby="timed-tab-actions"
        >
          <p className="text-sm text-base-content/70">{t('timedCount.playersIntro')}</p>
          {data?.seats.length === 0 && (
            <p className="text-sm text-base-content">{t('timedCount.noPlayers')}</p>
          )}
          {data?.seats.map((seat) => (
            <div
              key={seat.memberNo}
              className="border border-base-300 p-3 text-sm text-base-content"
            >
              <p className="font-medium">{seatIdentity(seat)}</p>
              <p className="tabular-nums">
                {seat.tableNo && seat.seatNo
                  ? t('timedCount.seatPlace', { tableNo: seat.tableNo, seatNo: seat.seatNo })
                  : t('timedCount.unseated')}{' '}
                ·{' '}
                {seat.countRevisionId
                  ? t('timedCount.countedTotal', { total: figure(seat.totalChips) })
                  : t('timedCount.notCounted')}
              </p>
            </div>
          ))}
          <Link to={`/tournament/${tournamentId}/timed-settlement`} className={OUTLINE}>
            {t('timedCount.editPlayers')}
          </Link>
        </div>
      )}
      {activeTab === 'transfer' && (
        <div
          className="space-y-3"
          role="tabpanel"
          id="timed-panel-transfer"
          aria-labelledby="timed-tab-transfer"
        >
          <p className="text-sm text-base-content/70">{t('timedCount.transferIntro')}</p>
          <label className="block text-sm text-base-content">
            {t('timedCount.tablePlayers')}
            <select
              className={FIELD}
              value={transferMemberNo}
              onChange={(event) => setTransferMemberNo(event.target.value)}
            >
              <option value="">{t('timedCount.choosePlayer')}</option>
              {tableSeats.map((seat) => (
                <option key={seat.memberNo} value={seat.memberNo}>
                  {t('timedCount.seatOption', { seatNo: seat.seatNo, player: seatIdentity(seat) })}
                </option>
              ))}
            </select>
          </label>
          <label className="block text-sm text-base-content">
            {t('timedCount.targetTable')}
            <select
              className={FIELD}
              value={targetTableId}
              onChange={(event) => {
                setTargetTableId(event.target.value);
                setTargetSeatNo(null);
              }}
            >
              <option value="">{t('timedCount.chooseTable')}</option>
              {assignedTables
                .filter((table) => table.id !== tableId)
                .map((table) => (
                  <option key={table.id} value={table.id}>
                    {t('timedCount.tableOption', { tableNo: table.tableNo })}
                  </option>
                ))}
            </select>
          </label>
          <label className="block text-sm text-base-content">
            {t('timedCount.targetSeat')}
            <select
              className={FIELD}
              value={targetSeatNo ?? ''}
              onChange={(event) => setTargetSeatNo(Number(event.target.value) || null)}
              disabled={!targetTableId}
            >
              <option value="">{t('timedCount.chooseSeat')}</option>
              {targetSeats.map((number) => (
                <option key={number} value={number}>
                  {t('timedCount.seatNo', { seatNo: number })}
                </option>
              ))}
            </select>
          </label>
          {targetTableId && targetSeats.length === 0 && (
            <p role="status" className="border border-warning p-2 text-sm text-base-content">
              {t('timedCount.noTargetSeats')}
            </p>
          )}
          <label className="block text-sm text-base-content">
            {t('timedCount.transferReason')}
            <input
              className={FIELD}
              value={transferReason}
              onChange={(event) => setTransferReason(event.target.value)}
            />
          </label>
          <button
            type="button"
            className={PRIMARY}
            disabled={
              !canCount ||
              !transferMemberNo ||
              !targetTableId ||
              !targetSeatNo ||
              !transferReason.trim() ||
              transfer.isPending
            }
            onClick={() => transfer.mutate()}
          >
            {t('timedCount.saveTransfer')}
          </button>
        </div>
      )}
      {error && (
        <p role="alert" className="text-error text-sm">
          {error}
        </p>
      )}
      {success && (
        <p role="status" className="text-success text-sm">
          {success}
        </p>
      )}
      <Link to={`/tournament/${tournamentId}/timed-settlement`} className={OUTLINE}>
        {t('timedCount.backToSettlement')}
      </Link>
    </section>
  );
}
